export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true; // Check for reference equality

  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false; // Not the same type or one is null
  }

  // Check if both are arrays
  const isArrayA = Array.isArray(a);
  const isArrayB = Array.isArray(b);

  if (isArrayA !== isArrayB) return false; // One is an array, the other is not

  // Handle arrays
  if (isArrayA && isArrayB) {
    if (a.length !== b.length) return false; // Different lengths

    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) {
        return false; // Element not equal
      }
    }
    return true; // All checks passed
  }

  // Handle objects
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false; // Different number of keys

  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) {
      return false; // Key not found or values are not equal
    }
  }

  return true; // All checks passed
}

export function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export function deepMerge<T>(target: T, ...sources: Partial<T>[]): T {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return deepMerge(target, ...sources);
}

export function sortBy(key: string, cb: (a?: number, b?: number) => number) {
  if (!cb) cb = () => 0;
  return (a?: number, b?: number) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : cb(a, b));
}

export function sortByDesc(key: string, cb: (a?: number, b?: number) => number) {
  if (!cb) cb = () => 0;
  return (b?: number, a?: number) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : cb(b, a));
}

export function orderBy(keys: string[], orders: ('asc' | 'desc')[]) {
  let cb = () => 0;
  keys.reverse();
  orders.reverse();
  for (const [i, key] of keys.entries()) {
    const order = orders[i];

    if (order === 'asc') cb = sortBy(key, cb);
    else if (order === 'desc') cb = sortByDesc(key, cb);
  }
  return cb;
}

export const differenceWith =
  (...arrays) =>
  (fn) => {
    return arrays.reduce((a, b) => {
      return a.filter((c) => !b.find((d) => fn(d, c)));
    });
  };

export function intersection<T>(arr1: T[], arr2: T[]): T[] {
  return arr1.filter((item) => arr2.includes(item));
}
